import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../../lib/api';
import { formatDateTime } from '../../lib/formatUtils';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Clock, AlertTriangle, CheckCircle2, CalendarDays, Phone, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { SmbOutreachAutomationBanner } from '../../components/smb/SmbOutreachAutomationBanner';

export const SMBFollowups = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('today');
  const [followups, setFollowups] = useState([]);
  const [counts, setCounts] = useState({ today: 0, overdue: 0, upcoming: 0 });

  const fetchFollowups = useCallback(async () => {
    try {
      const res = await api.get(`/smb/followups?filter=${tab}`);
      if (res.data.success) setFollowups(res.data.data);
    } catch { toast.error('Failed to load'); }
  }, [tab]);

  const fetchCounts = useCallback(async () => {
    try {
      const [t, o, u] = await Promise.all([
        api.get('/smb/followups?filter=today'),
        api.get('/smb/followups?filter=overdue'),
        api.get('/smb/followups?filter=upcoming'),
      ]);
      setCounts({
        today: t.data.success ? t.data.data.length : 0,
        overdue: o.data.success ? o.data.data.length : 0,
        upcoming: u.data.success ? u.data.data.length : 0,
      });
    } catch {}
  }, []);

  useEffect(() => { fetchFollowups(); }, [fetchFollowups]);
  useEffect(() => { fetchCounts(); }, [fetchCounts]);

  const handleComplete = async (fId) => {
    try {
      await api.put(`/smb/followups/${fId}/complete`);
      toast.success('Completed');
      fetchFollowups(); fetchCounts();
    } catch { toast.error('Failed'); }
  };

  const isOverdue = (dt) => new Date(dt) < new Date();

  return (
    <div className="space-y-5 animate-fade-in" data-testid="smb-followups">
      <SmbOutreachAutomationBanner />
      <div>
        <h1 className="text-2xl font-bold">Follow-ups</h1>
        <p className="text-muted-foreground text-sm">Track and manage follow-up tasks</p>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <Card className={`cursor-pointer transition-colors ${tab === 'overdue' ? 'border-red-500/50' : ''}`} onClick={() => setTab('overdue')} data-testid="count-overdue">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-red-500/10 flex items-center justify-center"><AlertTriangle className="h-5 w-5 text-red-500" /></div>
            <div><p className="text-2xl font-bold text-red-400">{counts.overdue}</p><p className="text-xs text-muted-foreground">Overdue</p></div>
          </CardContent>
        </Card>
        <Card className={`cursor-pointer transition-colors ${tab === 'today' ? 'border-primary/50' : ''}`} onClick={() => setTab('today')} data-testid="count-today">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center"><Clock className="h-5 w-5 text-primary" /></div>
            <div><p className="text-2xl font-bold">{counts.today}</p><p className="text-xs text-muted-foreground">Today</p></div>
          </CardContent>
        </Card>
        <Card className={`cursor-pointer transition-colors ${tab === 'upcoming' ? 'border-cyan-500/50' : ''}`} onClick={() => setTab('upcoming')} data-testid="count-upcoming">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-cyan-500/10 flex items-center justify-center"><CalendarDays className="h-5 w-5 text-cyan-500" /></div>
            <div><p className="text-2xl font-bold">{counts.upcoming}</p><p className="text-xs text-muted-foreground">Upcoming</p></div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="followup-tabs">
          <TabsTrigger value="overdue">Overdue ({counts.overdue})</TabsTrigger>
          <TabsTrigger value="today">Today ({counts.today})</TabsTrigger>
          <TabsTrigger value="upcoming">Upcoming ({counts.upcoming})</TabsTrigger>
        </TabsList>

        {['overdue', 'today', 'upcoming'].map(tabKey => (
          <TabsContent key={tabKey} value={tabKey}>
            {followups.length === 0 ? (
              <Card><CardContent className="py-12 text-center text-muted-foreground">
                <CheckCircle2 className="h-10 w-10 mx-auto mb-3 text-green-500/50" />
                <p>No {tabKey} follow-ups</p>
              </CardContent></Card>
            ) : (
              <div className="space-y-3">
                {followups.map(f => (
                  <Card key={f.id} className={`transition-colors ${tabKey === 'overdue' ? 'border-red-500/30' : ''}`} data-testid={`followup-card-${f.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-4">
                        <div className={`h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0 ${tabKey === 'overdue' ? 'bg-red-500/10' : 'bg-primary/10'}`}>
                          {tabKey === 'overdue' ? <AlertTriangle className="h-5 w-5 text-red-500" /> : <Clock className="h-5 w-5 text-primary" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <Link
                              to={`/smb/leads/${f.lead_id}`}
                              className="font-medium text-sm hover:text-primary hover:underline"
                              data-testid={`followup-name-link-${f.id}`}
                            >
                              {f.lead_business_name}
                            </Link>
                            <Badge variant="outline" className="text-xs capitalize">{f.lead_status}</Badge>
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">{f.note}</p>
                          <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{formatDateTime(f.followup_datetime)}</span>
                            {f.lead_phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{f.lead_contact_name}</span>}
                            {f.assigned_to_name && <span>{f.assigned_to_name}</span>}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {f.lead_phone && (
                            <Button size="sm" variant="outline" className="h-8" onClick={() => window.open(`tel:${f.lead_phone}`)} data-testid={`call-${f.id}`}>
                              <Phone className="h-3.5 w-3.5 mr-1" />Call
                            </Button>
                          )}
                          <Button asChild size="sm" variant="outline" className="h-8" data-testid={`view-lead-${f.id}`}>
                            <Link to={`/smb/leads/${f.lead_id}`}>
                              <ArrowRight className="h-3.5 w-3.5" />
                            </Link>
                          </Button>
                          {f.status === 'pending' && (
                            <Button size="sm" className="h-8" onClick={() => handleComplete(f.id)} data-testid={`complete-${f.id}`}>
                              <CheckCircle2 className="h-3.5 w-3.5 mr-1" />Done
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};
